import { Component, OnInit, ViewChild } from '@angular/core';
import Swal from 'sweetalert2';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { FormControl } from '@angular/forms';
import dayjs from 'dayjs';
import { getFirebaseBackend } from 'src/app/authUtils';
import { SupportTicketService } from 'src/app/services/support-ticket/support-ticket.service';
import { LeadService } from 'src/app/services/lead/lead.service';
import { RescheduleService } from 'src/app/services/rescheduled/reschedule.service';
import { ReplacementService } from 'src/app/services/replacement/replacement.service';
import { LifecycleCardsComponent } from 'src/app/shared/lead-edit/lead-details/lifecycle-cards/lifecycle-cards.component';

@Component({
	selector: 'app-support-tickets',
	templateUrl: './support-tickets.component.html',
	styleUrls: ['./support-tickets.component.scss'],
})
export class SupportTicketsComponent implements OnInit {
	loading = false;
	sending = false;
	changingStatus = false;
	rows: any[] = [];
	filteredRows: any[] = [];
	selectedTicket: any = null;
	ticketChats: any[] = [];
	loadingChats = false;
	messageSent = false;
	answerMessage = new FormControl('');
	currentUserId: number = getFirebaseBackend()?.getAuthenticatedUser()?.IdUser;
	activeTab = 1;
	leadDetail: any = null;
	loadingLead = false;
	showLifecycleSidebar = true;
	rescheduleDate = '';
	rescheduleHour = '12';
	rescheduleMin = '00';
	rescheduleAmPm: 'AM' | 'PM' = 'AM';
	rescheduleLoading = false;
	rescheduleFreeLoading = false;
	rescheduleChargeLoading = false;
	denyRescheduleLoading = false;

	replacementLeads: any[] = [];
	selectedReplacementAlias = '';
	replacementLoading = false;
	loadingReplacements = false;
	unreplaceLoading = false;
	replacementFreeLoading = false;
	replacementChargeLoading = false;
	denyReplacementLoading = false;

	currentPage = 1;
	pageSize = 20;

	statusFilter: 'open' | 'resolved' | 'all' = 'open';
	searchText = '';

	get totalPages(): number {
		return Math.ceil(this.rows.length / this.pageSize);
	}

	get pagedRows(): any[] {
		const start = (this.currentPage - 1) * this.pageSize;
		return this.rows.slice(start, start + this.pageSize);
	}

	get pageNumbers(): number[] {
		return Array.from({ length: this.totalPages }, (_, i) => i + 1);
	}

	min(a: number, b: number): number {
		return Math.min(a, b);
	}

	goToPage(page: number): void {
		if (page < 1 || page > this.totalPages) return;
		this.currentPage = page;
	}

	dateRange_ = {
		dateStart: dayjs().subtract(30, 'days'),
		dateEnd: dayjs(),
	};

	fromDate = dayjs().subtract(30, 'days').format('YYYY-MM-DD') + ' 00:00:00';
	toDate = dayjs().format('YYYY-MM-DD') + ' 23:59:59';

	@ViewChild('ticketModal') ticketModalRef: any;
	@ViewChild(LifecycleCardsComponent) lifecycleCards: LifecycleCardsComponent;

	get isLeadTicket(): boolean {
		const type = this.selectedTicket?.type?.toLowerCase();
		return type === 'reschedule' || type === 'replacement';
	}

	constructor(
		private supportTicketService: SupportTicketService,
		private leadService: LeadService,
		private rescheduleService: RescheduleService,
		private replacementSrv: ReplacementService,
		private modalService: NgbModal,
	) {}

	ngOnInit(): void {
		const ticketIdFromNotification = history.state?.ticketId;
		this.loadTickets(ticketIdFromNotification);
	}

	dateSelected(dates: any) {
		this.fromDate = dayjs(dates.dateStart).format('YYYY-MM-DD') + ' 00:00:00';
		this.toDate = dayjs(dates.dateEnd).format('YYYY-MM-DD') + ' 23:59:59';
	}

	loadTickets(openTicketId?: number) {
		this.loading = true;
		this.supportTicketService.getAllTickets(this.fromDate, this.toDate).subscribe({
			next: (data: any) => {
				this.filteredRows = data.body ?? data;
				this.applyFilters();
				this.loading = false;

				if (openTicketId) {
					this.openTicketFromNotification(openTicketId);
				}
			},
			error: () => {
				this.loading = false;
			},
		});
	}

	openTicket(ticket: any, content: any) {
		this.selectedTicket = ticket;
		this.ticketChats = [];
		this.messageSent = false;
		this.answerMessage.reset();
		this.activeTab = 1;
		this.leadDetail = null;

		this.loadTicketChats(ticket.id);
		this.replacementLeads = [];
		this.selectedReplacementAlias = '';

		const type = ticket.type?.toLowerCase();
		if ((type === 'reschedule' || type === 'replacement') && ticket.lead_id) {
			this.loadingLead = true;
			this.leadService.lead_detail(ticket.lead_id).subscribe({
				next: (data: any) => {
					this.leadDetail = data.body ?? data;
					this.loadingLead = false;
					if (type === 'replacement' && this.leadDetail?.replacement_status === 'Pending Replacement') {
						this.loadPossibleReplacements();
					}
				},
				error: () => {
					this.loadingLead = false;
				},
			});
		}

		this.modalService.open(content, {
			ariaLabelledBy: 'modal-ticket-title',
			centered: true,
			size: type === 'reschedule' || type === 'replacement' ? 'xl' : 'lg',
		});
	}

	loadTicketChats(ticketId: number) {
		this.loadingChats = true;
		this.supportTicketService.getTicketChats(ticketId).subscribe({
			next: (data: any) => {
				this.ticketChats = data.body ?? data;
				this.loadingChats = false;
			},
			error: () => {
				this.loadingChats = false;
			},
		});
	}

	sendAnswer() {
		const message = this.answerMessage.value?.trim();
		if (!message) return;

		const userId = getFirebaseBackend()?.getAuthenticatedUser()?.IdUser;
		this.sending = true;

		this.supportTicketService.answerTicket(this.selectedTicket.id, message, userId).subscribe({
			next: () => {
				this.sending = false;
				this.messageSent = true;
				this.answerMessage.reset();
				this.loadTicketChats(this.selectedTicket.id);
			},
			error: () => {
				this.sending = false;
			},
		});
	}

	// updateFilter(event: Event) {
	// 	const val = (event.target as HTMLInputElement).value.toLowerCase();
	// 	if (val) {
	// 		this.rows = this.filteredRows.filter((row) => (row.title && row.title.toLowerCase().includes(val)) || (row.status && row.status.toLowerCase().includes(val)) || (row.waiting_for && row.waiting_for.toLowerCase().includes(val)));
	// 	} else {
	// 		this.rows = this.filteredRows;
	// 	}
	// 	this.currentPage = 1;
	// }

	updateFilter(event: Event) {
		this.searchText = (event.target as HTMLInputElement).value.toLowerCase();
		this.applyFilters();
	}

		applyFilters() {
		const val = this.searchText.replace('#', '').trim();
		this.rows = this.filteredRows.filter((row) => {
			const matchesStatus = this.statusFilter === 'all' || (this.statusFilter === 'resolved' ? row.status === 'resolved' : row.status !== 'resolved');

			const matchesText = !val || String(row.id).includes(val) || (row.reference && row.reference.toLowerCase().includes(val)) || (row.title && row.title.toLowerCase().includes(val)) || (row.status && row.status.toLowerCase().includes(val)) || (row.waiting_for && row.waiting_for.toLowerCase().includes(val));

			return matchesStatus && matchesText;
		});
		this.currentPage = 1;
	}

	confirmChangeStatus() {
		const isResolved = this.selectedTicket?.status === 'resolved';
		const newStatus = isResolved ? 'open' : 'resolved';

		Swal.fire({
			title: 'Are you sure?',
			text: isResolved ? 'This ticket will be reopened and marked as Open.' : 'This ticket will be marked as Resolved.',
			icon: 'warning',
			showCancelButton: true,
			confirmButtonColor: isResolved ? '#ffc107' : '#dc3545',
			cancelButtonColor: '#6c757d',
			confirmButtonText: isResolved ? 'Yes, reopen it' : 'Yes, mark as resolved',
			cancelButtonText: 'Cancel',
		}).then((result) => {
			if (result.isConfirmed) {
				this.changeTicketStatus(newStatus);
			}
		});
	}

	changeTicketStatus(status: string) {
		this.changingStatus = true;
		this.supportTicketService.changeStatus(this.selectedTicket.id, status).subscribe({
			next: () => {
				this.selectedTicket = { ...this.selectedTicket, status };
				const idx = this.rows.findIndex((r: any) => r.id === this.selectedTicket.id);
				if (idx !== -1) {
					this.rows[idx] = { ...this.rows[idx], status };
					this.rows = [...this.rows];
				}
				this.changingStatus = false;
				Swal.fire('Done!', `Ticket marked as ${status}.`, 'success');
			},
			error: () => {
				this.changingStatus = false;
				Swal.fire('Error', 'Could not update ticket status.', 'error');
			},
		});
	}

	isReplacement(ticket: any): boolean {
		return ticket?.type?.toLowerCase() === 'replacement';
	}

	isReschedule(ticket: any): boolean {
		return ticket?.type?.toLowerCase() === 'reschedule';
	}

	getWaitingForClass(value: string): string {
		if (!value) return 'badge-secondary';
		switch (value.toLowerCase()) {
			case 'admin':
				return 'badge-primary';
			case 'customer':
				return 'badge-danger';
			default:
				return 'badge-secondary';
		}
	}

	/** Re-fetches the lead behind the open ticket — used after a reschedule action so the modal reflects the real, backend-side status instead of a guessed local patch. */
	reloadLeadDetail(): void {
		const leadId = this.selectedTicket?.lead_id;
		if (!leadId) return;
		this.loadingLead = true;
		this.leadService.lead_detail(leadId).subscribe({
			next: (data: any) => {
				this.leadDetail = data.body ?? data;
				this.loadingLead = false;
				// A free-approved replacement lands here needing a replacement lead assigned — load the picker without waiting for the modal to be reopened.
				if (this.leadDetail?.replacement_status === 'Pending Replacement') {
					this.loadPossibleReplacements();
				}
			},
			error: () => {
				this.loadingLead = false;
			},
		});
	}

	/** Re-fetches the ticket itself (status/waiting_for can change once a replacement is approved/denied) and keeps the list rows in sync. */
	reloadTicket(): void {
		const ticketId = this.selectedTicket?.id;
		if (!ticketId) return;
		this.supportTicketService.getTicketById(ticketId).subscribe({
			next: (data: any) => {
				const updated = data.body ?? data;
				this.selectedTicket = updated;
				const idx = this.rows.findIndex((r: any) => r.id === ticketId);
				if (idx !== -1) this.rows = [...this.rows.slice(0, idx), updated, ...this.rows.slice(idx + 1)];
				const fIdx = this.filteredRows.findIndex((r: any) => r.id === ticketId);
				if (fIdx !== -1) this.filteredRows = [...this.filteredRows.slice(0, fIdx), updated, ...this.filteredRows.slice(fIdx + 1)];
			},
		});
	}

	submitReschedule() {
		if (!this.rescheduleDate) {
			Swal.fire('Missing info', 'Please select a date.', 'warning');
			return;
		}

		let hour24 = parseInt(this.rescheduleHour, 10);
		if (this.rescheduleAmPm === 'PM' && hour24 !== 12) hour24 += 12;
		if (this.rescheduleAmPm === 'AM' && hour24 === 12) hour24 = 0;
		const newTime = `${String(hour24).padStart(2, '0')}:${this.rescheduleMin}:00`;
		const newDate = `${this.rescheduleDate} ${newTime}`;
		const displayTime = `${this.rescheduleHour}:${this.rescheduleMin} ${this.rescheduleAmPm}`;

		const user = getFirebaseBackend()?.getAuthenticatedUser();

		Swal.fire({
			title: 'Confirm Reschedule',
			html: `The lead <strong>#${this.selectedTicket.lead_id}</strong> will be rescheduled to <strong>${this.rescheduleDate} ${displayTime}</strong>.<br/><br/>Continue?`,
			icon: 'question',
			showCancelButton: true,
			confirmButtonText: 'Yes, reschedule',
			cancelButtonText: 'Cancel',
		}).then((result) => {
			if (!result.isConfirmed) return;
			this.rescheduleLoading = true;

			const payload = {
				id: null,
				lead_id: this.selectedTicket.lead_id,
				alias: user?.Login ?? '',
				new_date: newDate,
				newHour: this.rescheduleHour.padStart(2, '0'),
				newMin: this.rescheduleMin,
				ampm: this.rescheduleAmPm,
				reason: '',
				status: 'Pending',
				resolved_by: user?.IdUser ?? null,
				newTime,
				calendar_id: this.leadDetail?.calendar_id ?? null,
				user_login: user?.Login ?? '',
				user_id: user?.IdUser ?? null,
				send_notification: false,
			};

			this.leadService.rescheduleQa(payload).subscribe({
				next: () => {
					this.rescheduleLoading = false;
					this.reloadLeadDetail();
					Swal.fire('Done!', 'Lead rescheduled successfully.', 'success');
					this.lifecycleCards?.loadEvents();
				},
				error: () => {
					this.rescheduleLoading = false;
					Swal.fire('Error', 'Could not reschedule the lead.', 'error');
				},
			});
		});
	}

	confirmRescheduleFree() {
		const user = getFirebaseBackend()?.getAuthenticatedUser();
		Swal.fire({
			title: 'Approve Reschedule for FREE',
			text: 'Are you sure to approve this reschedule as free?',
			icon: 'question',
			showCancelButton: true,
			confirmButtonText: 'Yes, approve',
			cancelButtonText: 'Cancel',
		}).then((result) => {
			if (!result.isConfirmed) return;
			this.rescheduleFreeLoading = true;
			this.rescheduleService.setRescheduleFree(this.selectedTicket.lead_id, this.leadDetail?.alias, user?.IdUser).subscribe({
				next: () => {
					this.rescheduleFreeLoading = false;
					this.reloadLeadDetail();
					Swal.fire('Success', 'Reschedule approved as free.', 'success');
					this.lifecycleCards?.loadEvents();
				},
				error: () => {
					this.rescheduleFreeLoading = false;
					Swal.fire('Error', 'Could not approve the reschedule.', 'error');
				},
			});
		});
	}

	confirmRescheduleCharge() {
		if (isNaN(this.leadDetail?.reschedule_cost) || this.leadDetail?.reschedule_cost <= 0) {
			Swal.fire('Error on Reschedule cost', `The reschedule cost has an error, this is the value obtained: "${this.leadDetail?.reschedule_cost}" please contact admin.`, 'error');
			return;
		}
		const user = getFirebaseBackend()?.getAuthenticatedUser();
		Swal.fire({
			title: 'Approve Reschedule with Fee',
			text: `Are you sure to charge $${this.leadDetail.reschedule_cost} for this reschedule?`,
			icon: 'question',
			showCancelButton: true,
			confirmButtonText: 'Send notification to the client',
			cancelButtonText: 'Cancel',
		}).then((result) => {
			if (!result.isConfirmed) return;
			this.rescheduleChargeLoading = true;
			this.rescheduleService.setRescheduleCharge(this.selectedTicket.lead_id, this.leadDetail.reschedule_cost, this.leadDetail?.alias, user?.IdUser).subscribe({
				next: () => {
					this.rescheduleChargeLoading = false;
					this.reloadLeadDetail();
					Swal.fire('Success', 'Reschedule approved with fee — the client has been notified.', 'success');
					this.lifecycleCards?.loadEvents();
				},
				error: () => {
					this.rescheduleChargeLoading = false;
					Swal.fire('Error', 'Could not approve the reschedule.', 'error');
				},
			});
		});
	}

	denyReschedule() {
		const user = getFirebaseBackend()?.getAuthenticatedUser();
		Swal.fire({
			title: 'Deny Reschedule',
			text: 'Are you sure to deny this reschedule request?',
			icon: 'warning',
			showCancelButton: true,
			confirmButtonText: 'Yes, deny',
			cancelButtonText: 'Cancel',
		}).then((result) => {
			if (!result.isConfirmed) return;
			this.denyRescheduleLoading = true;
			this.rescheduleService.denyReschedule(this.selectedTicket.lead_id, this.leadDetail?.alias, user?.IdUser).subscribe({
				next: () => {
					this.denyRescheduleLoading = false;
					this.reloadLeadDetail();
					Swal.fire('Done!', 'Reschedule request denied.', 'success');
					this.lifecycleCards?.loadEvents();
				},
				error: () => {
					this.denyRescheduleLoading = false;
					Swal.fire('Error', 'Could not deny the reschedule.', 'error');
				},
			});
		});
	}

	confirmReplacementFree() {
		Swal.fire({
			title: 'Approve Replacement for FREE',
			text: 'Are you sure to approve this replacement as free?',
			icon: 'question',
			showCancelButton: true,
			confirmButtonText: 'Yes, approve',
			cancelButtonText: 'Cancel',
		}).then((result) => {
			if (!result.isConfirmed) return;
			this.replacementFreeLoading = true;
			this.replacementSrv.setReplacementFree(this.selectedTicket.lead_id, this.leadDetail?.alias, this.currentUserId).subscribe({
				next: () => {
					this.replacementFreeLoading = false;
					this.reloadLeadDetail();
					this.reloadTicket();
					Swal.fire('Success', 'Replacement approved as free.', 'success');
					this.lifecycleCards?.loadEvents();
				},
				error: () => {
					this.replacementFreeLoading = false;
					Swal.fire('Error', 'Could not approve the replacement.', 'error');
				},
			});
		});
	}

	confirmReplacementCharge() {
		if (isNaN(this.leadDetail?.replacement_cost) || this.leadDetail?.replacement_cost <= 0) {
			Swal.fire('Error on Replacement cost', `The replacement cost has an error, this is the value obtained: "${this.leadDetail?.replacement_cost}" please contact admin.`, 'error');
			return;
		}
		Swal.fire({
			title: 'Approve Replacement with Fee',
			text: `Are you sure to charge $${this.leadDetail.replacement_cost} for this replacement?`,
			icon: 'question',
			showCancelButton: true,
			confirmButtonText: 'Send notification to the client',
			cancelButtonText: 'Cancel',
		}).then((result) => {
			if (!result.isConfirmed) return;
			this.replacementChargeLoading = true;
			this.replacementSrv.setReplacementCharge(this.selectedTicket.lead_id, this.leadDetail.replacement_cost, this.leadDetail?.alias, this.currentUserId).subscribe({
				next: () => {
					this.replacementChargeLoading = false;
					this.reloadLeadDetail();
					this.reloadTicket();
					Swal.fire('Success', 'Replacement approved with fee — the client has been notified.', 'success');
					this.lifecycleCards?.loadEvents();
				},
				error: () => {
					this.replacementChargeLoading = false;
					Swal.fire('Error', 'Could not approve the replacement.', 'error');
				},
			});
		});
	}

	denyReplacement() {
		Swal.fire({
			title: 'Deny Replacement',
			text: 'Are you sure to deny this replacement request?',
			icon: 'warning',
			showCancelButton: true,
			confirmButtonText: 'Yes, deny',
			cancelButtonText: 'Cancel',
		}).then((result) => {
			if (!result.isConfirmed) return;
			this.denyReplacementLoading = true;
			this.replacementSrv.denyReplacement(this.selectedTicket.lead_id, this.leadDetail?.alias, this.currentUserId).subscribe({
				next: () => {
					this.denyReplacementLoading = false;
					this.reloadLeadDetail();
					this.reloadTicket();
					Swal.fire('Done!', 'Replacement request denied.', 'success');
					this.lifecycleCards?.loadEvents();
				},
				error: () => {
					this.denyReplacementLoading = false;
					Swal.fire('Error', 'Could not deny the replacement.', 'error');
				},
			});
		});
	}

	submitUnreplace() {
		Swal.fire({
			title: 'Undo Replacement?',
			html: `Lead <strong>#${this.selectedTicket.lead_id}</strong> will be unreplaced.<br/><br/>Continue?`,
			icon: 'warning',
			showCancelButton: true,
			confirmButtonText: 'Yes, unreplace',
			cancelButtonText: 'Cancel',
		}).then((result) => {
			if (!result.isConfirmed) return;
			this.unreplaceLoading = true;
			this.leadService.unreplaceLead(this.selectedTicket.lead_id, this.currentUserId).subscribe({
				next: () => {
					this.unreplaceLoading = false;
					this.reloadLeadDetail();
					this.reloadTicket();
					Swal.fire('Done!', 'Lead unreplaced successfully.', 'success');
					this.lifecycleCards?.loadEvents();
				},
				error: () => {
					this.unreplaceLoading = false;
					Swal.fire('Error', 'Could not unreplace the lead.', 'error');
				},
			});
		});
	}

	loadPossibleReplacements() {
		if (!this.leadDetail?.alias) return;
		this.loadingReplacements = true;
		this.leadService.getPossibleReplacements(this.leadDetail.alias).subscribe({
			next: (data: any) => {
				this.replacementLeads = data.body ?? data;
				this.loadingReplacements = false;
			},
			error: () => {
				this.loadingReplacements = false;
			},
		});
	}

	submitReplacement() {
		if (!this.selectedReplacementAlias) {
			Swal.fire('Missing info', 'Please select a replacement lead.', 'warning');
			return;
		}
		const user = getFirebaseBackend()?.getAuthenticatedUser();
		Swal.fire({
			title: 'Confirm Replacement',
			html: `Lead <strong>#${this.selectedTicket.lead_id}</strong> will be replaced by <strong>${this.selectedReplacementAlias}</strong>.<br/><br/>Continue?`,
			icon: 'question',
			showCancelButton: true,
			confirmButtonText: 'Yes, replace',
			cancelButtonText: 'Cancel',
		}).then((result) => {
			if (!result.isConfirmed) return;
			this.replacementLoading = true;
			const payload = {
				lead_id: this.selectedTicket.lead_id,
				replaced_by: this.selectedReplacementAlias,
				replacement_status: null,
				user: user?.Login ?? '',
				user_id: user?.IdUser ?? null,
			};
			this.leadService.replaceLeadBy(payload).subscribe({
				next: () => {
					this.replacementLoading = false;
					this.reloadLeadDetail();
					this.reloadTicket();
					Swal.fire('Done!', 'Lead replaced successfully.', 'success');
					this.lifecycleCards?.loadEvents();
				},
				error: () => {
					this.replacementLoading = false;
					Swal.fire('Error', 'Could not replace the lead.', 'error');
				},
			});
		});
	}

	formatPhone(raw: string): string {
		if (!raw) return '—';
		const digits = raw.replace(/\D/g, '');
		if (digits.length < 10) return raw;
		return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
	}

	getStatusClass(status: string): string {
		if (!status) return 'badge-secondary';
		switch (status.toLowerCase()) {
			case 'open':
				return 'badge-primary';
			case 'closed':
				return 'badge-success';
			case 'pending':
				return 'badge-warning';
			case 'in_progress':
				return 'badge-info';
			default:
				return 'badge-secondary';
		}
	}

	private openTicketFromNotification(ticketId: number): void {
		const ticket = this.rows.find((t) => t.id === ticketId);
		if (ticket) {
			this.openTicket(ticket, this.ticketModalRef);
		}
	}
}
